import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { useBroadcastChannel } from 'src/hooks/use-broadcast-channel';
import {
  ShowHelpAnnotationsProvider,
  useShowHelpAnnotations,
} from 'src/hooks/use-help-annotations';
import { useSafeLocalStorage } from 'src/hooks/use-storage';
import { HelpAnnotationsDebugStyle } from 'src/pages/developer-tools/help-annotations-debug-styles';

const channelName = 'prisma-help-annotations';

type HelpAnnotationsMessage = {
  event: 'update';
  helpAnnotations: string[];
};

export const useHelpAnnotationsChannel = (
  callback?: (message: HelpAnnotationsMessage) => void
) => {
  return useBroadcastChannel<HelpAnnotationsMessage>(channelName, callback);
};

export const ShowHelpAnnotations: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const defaultValue = useShowHelpAnnotations(); // can be set in Storybook addon
  const safeLocalStorage = useSafeLocalStorage();

  const [helpAnnotations, setHelpAnnotations] = useState<string[]>([]);

  useHelpAnnotationsChannel((message) =>
    setHelpAnnotations(message.helpAnnotations)
  );

  const value =
    defaultValue ||
    Boolean(safeLocalStorage.getItem('SHOW_HELP_ANNOTATION')) || // set in E2E tests
    Boolean(helpAnnotations.length);

  return (
    <ShowHelpAnnotationsProvider value={value}>
      {children}
      {Boolean(helpAnnotations.length) && (
        <HelpAnnotationsDebugStyle helpAnnotations={helpAnnotations} />
      )}
    </ShowHelpAnnotationsProvider>
  );
};
